import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Link, Button, TextField, CircularProgress } from '@mui/material'
import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment-timezone'
import { startValidateAttendanceRequest } from '../../Redux/Slice/commonSlice'
import AnimatedResultScreen from '../Inventory/AnimatedResultScreen'

const DetailRow = ({ label, value, href, last }) => (
    <TableRow
        sx={{
            '&:nth-of-type(even)': { backgroundColor: '#ffffff' },
            '&:hover': { backgroundColor: '#f6f8fa' },
            transition: 'background-color 0.2s ease',
        }}
    >
        <TableCell
            sx={{
                fontWeight: 600,
                borderBottom: last ? 'none' : '1px solid #e1e4e8',
                padding: { xs: '8px 12px', md: '10px 24px' },
                width: { xs: '35%', md: '40%' },
                color: '#24292e',
                fontSize: { xs: '0.8rem', md: '0.95rem' },
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </TableCell>
        <TableCell
            sx={{
                borderBottom: last ? 'none' : '1px solid #e1e4e8',
                padding: { xs: '8px 12px', md: '10px 24px' },
                color: '#586069',
                fontSize: { xs: '0.8rem', md: '0.95rem' },
                fontWeight: 500,
                wordBreak: 'break-word',
            }}
        >
            {href ? (
                <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
                    {value}
                </Link>
            ) : (
                value || '–'
            )}
        </TableCell>
    </TableRow>
)

const MAX_REJECT_REASON_LENGTH = 200

const ApprovalAttendanceRequest = () => {
    const dispatch = useDispatch()
    const [showRejectReason, setShowRejectReason] = useState(false)
    const [rejectReasonText, setRejectReasonText] = useState('')

    const company = useSelector(state => state.common?.company)
    const attendanceRequest = useSelector(state => state.common?.attendanceRequest)
    const buildingTimezone = useSelector(state => state.common?.buildingTimezone)
    const loading = useSelector(state => state.common?.attendanceRequestLoading)
    const success = useSelector(state => state.common?.attendanceRequestSuccess)
    const error = useSelector(state => state.common?.attendanceRequestError)
    const actionType = useSelector(state => state.common?.attendanceRequestActionType)
    const tzCode = buildingTimezone?.tzCode || 'UTC'

    const employeeName = attendanceRequest?.userDetails?.name ?? attendanceRequest?.employeeName ?? ''
    const employeeEmail = attendanceRequest?.userDetails?.email ?? attendanceRequest?.employeeEmail ?? ''
    const empCode = attendanceRequest?.empCode ?? attendanceRequest?.employeeCode ?? ''
    const workType = attendanceRequest?.workType ?? ''
    const workPeriod = attendanceRequest?.workPeriod ?? ''
    const checkIn = attendanceRequest?.originalCheckInTime ?? attendanceRequest?.checkIn ?? attendanceRequest?.checkInTime
    const checkOut = attendanceRequest?.originalCheckOutTime ?? attendanceRequest?.checkOut ?? attendanceRequest?.checkOutTime
    const reason = attendanceRequest?.requestReason ?? attendanceRequest?.reason ?? ''
    const approvalType = attendanceRequest?.approvalType ?? ''
    const approvalStatus = attendanceRequest?.approvalStatus ?? ''
    const attendanceDate = attendanceRequest?.attendanceDate ?? ''
    const workTypeDescription = attendanceRequest?.workTypeDescription ?? ''

    const formatDateTime = (ts) => {
        if (!ts) return '–'
        return moment(ts).tz(tzCode).format('MMM D, YYYY h:mm A')
    }

    const formatWorkPeriod = (period) => {
        if (!period) return '–'
        return period.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    }

    const rows = [
        { label: 'Employee', value: `${employeeName} (${employeeEmail})` },
        { label: 'Emp Code', value: empCode },
        // { label: 'Reporting Manager', value: reportingManagerName },
        { label: 'Attendance Date', value: attendanceDate ? moment(attendanceDate).format('MMM D, YYYY') : '–' },
        { label: 'Work Type', value: workType },
        { label: 'Work Period', value: formatWorkPeriod(workPeriod) },
        { label: 'Check-in', value: formatDateTime(checkIn) },
        { label: 'Check-out', value: formatDateTime(checkOut) },
        { label: 'Reason', value: reason },
        { label: 'Approval Type', value: approvalType },
        { label: 'Status', value: approvalStatus ? approvalStatus.charAt(0).toUpperCase() + approvalStatus.slice(1) : '–' },
        { label: 'Description', value: workTypeDescription },
    ]

    const dispatchAction = (status, rejectionReason = '') => {
        const attendanceApprovalId = attendanceRequest?._id
        if (!attendanceApprovalId) return
        dispatch(startValidateAttendanceRequest({
            attendanceApprovalId,
            body: {
                tenantId: company?.tenantCode,
                status,
                rejectionReason,
                approverName: attendanceRequest?.reportingManagerName ?? '',
                approverId: attendanceRequest?.reportingManagerId ?? '',
            }
        }))
    }

    const handleApprove = () => {
        setShowRejectReason(false)
        dispatchAction('approved')
    }

    const handleReject = () => {
        setShowRejectReason(true)
    }

    const handleRejectReasonChange = (e) => {
        if (e.target?.value?.length <= MAX_REJECT_REASON_LENGTH) {
            setRejectReasonText(e.target?.value ?? '')
        }
    }

    const handleRejectSubmit = () => {
        if (!rejectReasonText.trim()) return
        dispatchAction('rejected', rejectReasonText.trim())
    }

    const isActioned = attendanceRequest?.approvalStatus !== 'pending'

    if (success || error) {
        const resultMessage = success
            ? (actionType === 'approved' ? 'Attendance request Approved.' : 'Attendance request Rejected.')
            : error
        return (
            <Box sx={{
                height: '90vh',
                width: { xs: '100%', sm: '450px', md: '600px' },
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: { md: '0.5px solid grey' },
                borderRadius: { md: '10px' },
            }}>
                <Box sx={{
                    height: '10vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    pt: 4,
                }}>
                    <img
                        src={company?.logo}
                        alt="company_logo"
                        style={{ height: '36px' }}
                    />
                </Box>
                <Box sx={{
                    height: '60vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <AnimatedResultScreen
                        type={success ? (actionType === 'approved' ? 'success' : 'rejected') : 'rejected'}
                        message={resultMessage}
                    />
                </Box>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                minHeight: '90vh',
                width: { xs: '100%', sm: '450px', md: '600px' },
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                border: { md: '0.5px solid grey' },
                borderRadius: { md: '10px' },
                overflow: 'auto',
                boxSizing: 'border-box',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', pt: 2, pb: 1 }}>
                <img
                    src={company?.logo}
                    alt="company_logo"
                    style={{ height: '36px' }}
                />
            </Box>
            <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.25rem' }, color: '#24292e' }}>
                    Attendance Approval Request
                </Typography>
                <TableContainer
                    sx={{
                        backgroundColor: '#fafbfc',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid #e1e4e8',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                        mt: 2,
                        width: '100%',
                        maxWidth: { sm: 400, md: 480 },
                    }}
                >
                    <Table size="small">
                        <TableBody>
                            {rows.map((row, index) => (
                                <DetailRow
                                    key={row.label}
                                    label={row.label}
                                    value={row.value}
                                    href={row.href ?? null}
                                    last={index === rows.length - 1}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {!isActioned && !success && (
                    <>
                        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', width: { xs: '80%', sm: '50%' } }}>
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', gap: 2 }}>
                                <Button
                                    sx={{
                                        width: 100,
                                        bgcolor: '#F46326',
                                        color: '#fff',
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: '#dc4d28' },
                                        '&:disabled': { bgcolor: '#cccccc', color: '#666666' },
                                    }}
                                    onClick={handleApprove}
                                    disabled={loading}
                                >
                                    {loading && actionType === 'approved' ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Approve'}
                                </Button>
                                <Button
                                    sx={{
                                        width: 100,
                                        bgcolor: '#F46326',
                                        color: '#fff',
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: '#dc4d28' },
                                        '&:disabled': { bgcolor: '#cccccc', color: '#666666' },
                                    }}
                                    onClick={handleReject}
                                    disabled={loading}
                                >
                                    Reject
                                </Button>
                            </Box>
                        </Box>
                        {showRejectReason && (
                            <Box
                                sx={{
                                    mt: 4,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: '100%',
                                    maxWidth: { xs: '100%', sm: 400, md: 480 },
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <TextField
                                        label="Reason to reject (Required)"
                                        multiline
                                        rows={2}
                                        value={rejectReasonText}
                                        onChange={handleRejectReasonChange}
                                        required
                                        disabled={loading}
                                        error={!rejectReasonText.trim()}
                                        sx={{
                                            width: '100%',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                bgcolor: '#fafbfc',
                                                '&:hover fieldset': { borderColor: '#f44336' },
                                                '&.Mui-focused fieldset': { borderColor: '#f44336', borderWidth: '2px' },
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': { color: '#f44336' },
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            alignSelf: 'flex-end',
                                            mr: 1,
                                            color: rejectReasonText?.length === MAX_REJECT_REASON_LENGTH ? '#f44336' : '#6c757d',
                                            fontWeight: 500,
                                            fontSize: '0.8rem',
                                        }}
                                        variant="caption"
                                    >
                                        {rejectReasonText?.length}/{MAX_REJECT_REASON_LENGTH}
                                    </Typography>
                                </Box>
                                <Button
                                    sx={{
                                        height: 35,
                                        minWidth: 100,
                                        bgcolor: '#F46326',
                                        color: '#fff',
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: '#dc4d28' },
                                        '&:disabled': { bgcolor: '#cccccc', color: '#666666' },
                                    }}
                                    onClick={handleRejectSubmit}
                                    disabled={!rejectReasonText.trim() || loading}
                                >
                                    {loading && actionType === 'rejected' ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Submit'}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
}

export default ApprovalAttendanceRequest
